import React from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { supportsAnswerEvaluation } from 'utils/answerEvaluation';
import { hasQuestionGenerationCorrectness, supportsQuestionGeneration } from 'utils/questionGeneration';

import {
  AdminQuestion,
  AdminRoleplayPersona,
  AnswerReferenceWarning,
  DiffDetail,
  FlowOption,
  type MultipleSelectLimitInputs,
  type MultipleSelectLimitValidation,
  QUESTION_TYPE_OPTIONS,
  QuestionOptionEditable,
} from './types';
import type { QuestionHelperCopy } from '../../../components/questions/optionHelperText';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import MediaAssetPicker from '../../../components/admin/MediaAssetPicker';
import { MediaAsset, MediaAssetType } from 'types';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { ChevronDown, RefreshCw, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { OPTION_REQUIRED_TYPES } from './types';
import { renderDiffValue, stableStringify } from './utils';
import {
  mergeRoleplayFormValuesIntoMetadata,
  roleplayMetadataToFormValues,
  type RoleplayMetadataFormValues,
} from './roleplayMetadata';
import type {
  RoleplayScorecardSuggestionRequest,
  RoleplayVoiceOption,
} from 'services/api/adminContentClient';
import { RoleplayScorecardEditor } from './RoleplayScorecardEditor';
import { QuestionMarkdownEditor } from './QuestionMarkdownEditor';
import { QuestionPrompt, QuestionText, extractAnswerReferenceKeys, extractQuestionCitationIndices } from 'utils/questionMarkdown';
import { ANSWER_REFERENCE_PATTERN, ANSWER_REFERENCE_PREVIEW, AnswerReferenceContext } from 'utils/answerReferences';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { CitationList } from 'components/Citations/CitationList';
import { validateVoicePresentation } from 'utils/voicePresentation';
import { VoicePresentationEditor } from './VoicePresentationEditor';
import { moduleBuilderHref } from '../contentToolLinks';

export type QuestionLibraryEditorProps = {
  helperCopy?: QuestionHelperCopy;
  draft: AdminQuestion;
  keyError: string | null;
  selected: AdminQuestion | null;
  /** Links for attached modules known to belong to the current course; other attachments open standalone. */
  attachmentModuleLinks?: Record<number, string>;
  selectedQuestionTypeValue: string;
  questionTypeAvailabilityError?: string | null;
  onSelectQuestionType: (value: string) => void;
  onInputChange: (field: keyof AdminQuestion, value: unknown) => void;
  metadataValidationError: string | null;
  metadataParseError: string | null;
  onMetadataValidationChange: (error: string | null) => void;
  optionValidationError: string | null;
  onMediaAssetChange: (asset: MediaAsset | null) => void;
  restrictedMediaTypes?: MediaAssetType[];
  saving: boolean;
  multipleSelectLimitInputs: MultipleSelectLimitInputs;
  multipleSelectLimitValidation: MultipleSelectLimitValidation;
  onMultipleSelectLimitInputChange: (
    field: keyof MultipleSelectLimitInputs,
    value: string,
  ) => void;
  optionList: QuestionOptionEditable[];
  onAddOption: () => void;
  onUpdateOptionField: (index: number, patch: Partial<QuestionOptionEditable>) => void;
  onUpdateOptionValue: (index: number, value: string) => void;
  onUpdateOptionFlag: (
    index: number,
    field: 'is_correct' | 'is_other',
    checked: boolean | 'indeterminate',
  ) => void;
  onRemoveOption: (index: number) => void;
  attachableFlows: FlowOption[];
  filteredAttachableFlows: FlowOption[];
  attachFlowIds: string[];
  attachFlowRequiredById: Record<string, boolean>;
  onToggleAttachFlow: (flowId: number, checked: boolean | 'indeterminate') => void;
  onToggleAttachFlowRequired: (flowId: string, checked: boolean | 'indeterminate') => void;
  onRemoveAttachFlow: (flowId: string) => void;
  flowSearch: string;
  onFlowSearchChange: (value: string) => void;
  flows: FlowOption[];
  hasPreviewResult: boolean;
  diffPreview: string[];
  diffPreviewDetails: DiffDetail[];
  /** Earlier-answer references the review found unanswerable where this question is used. */
  answerReferenceWarnings?: AnswerReferenceWarning[];
  summaryItems: Array<{ key: string; label: string }>;
  formatFieldLabel: (field: string) => string;
  hasChanges: boolean;
  /** Computes the diff shown in the review dialog. Resolve `false` when validation stops the preview. */
  onPreview: () => void | Promise<boolean | void>;
  onSave: () => void | Promise<void>;
  /** Reverts unsaved edits to the last saved version (or clears a new draft). */
  onDiscard?: () => void;
  /** Reports edits that exist only inside the editor (staged roleplay form, unparsed metadata JSON). */
  onLocalDirtyChange?: (dirty: boolean) => void;
  onOpenRadarDefinition?: (definitionKey: string) => void;
  roleplayPersonas?: AdminRoleplayPersona[];
  roleplayVoiceOptions?: RoleplayVoiceOption[];
};

const ROLEPLAY_EXPRESSIONS = [
  ['at_ease', 'At ease'],
  ['guarded', 'Guarded'],
  ['anxious', 'Anxious'],
  ['sad', 'Sad'],
  ['overwhelmed', 'Overwhelmed'],
  ['ashamed', 'Ashamed'],
  ['defensive', 'Defensive'],
  ['angry', 'Angry'],
  ['numb', 'Numb'],
  ['vulnerable', 'Vulnerable'],
  ['relieved', 'Relieved'],
  ['hopeful', 'Hopeful'],
] as const;

const DEFAULT_ROLEPLAY_VOICE_OPTIONS: RoleplayVoiceOption[] = [
  { alias: 'marin', name: 'Marin', provider: 'openai' },
  { alias: 'cedar', name: 'Cedar', provider: 'openai' },
];

const MetadataEditor: React.FC<{
  value: Record<string, unknown> | undefined;
  onChange: (value: Record<string, unknown>) => void;
  validationError: string | null;
  onValidationChange: (error: string | null) => void;
  helpText?: string;
}> = ({ value, onChange, validationError, onValidationChange, helpText }) => {
  const [rawText, setRawText] = React.useState(() =>
    value && Object.keys(value).length > 0 ? JSON.stringify(value, null, 2) : '',
  );
  const [parseError, setParseError] = React.useState<string | null>(null);

  // Sync rawText when value changes externally (e.g. selecting a different question)
  const serialized = value && Object.keys(value).length > 0 ? JSON.stringify(value, null, 2) : '';
  React.useEffect(() => {
    if (parseError) {
      return;
    }
    setRawText(serialized);
    onValidationChange(null);
  }, [onValidationChange, parseError, serialized]);

  const handleChange = (text: string) => {
    setRawText(text);

    if (!text.trim()) {
      setParseError(null);
      onValidationChange(null);
      onChange({});
      return;
    }

    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        const nextError = 'Must be a JSON object (e.g. {"key": "value"})';
        setParseError(nextError);
        onValidationChange(nextError);
        return;
      }
      setParseError(null);
      onValidationChange(null);
      onChange(parsed as Record<string, unknown>);
    } catch {
      const nextError = 'Invalid JSON';
      setParseError(nextError);
      onValidationChange(nextError);
    }
  };

  const displayedError = parseError ?? validationError;

  return (
    <div className="space-y-1">
      <Textarea
        id="metadata"
        value={rawText}
        onChange={(event) => handleChange(event.target.value)}
        placeholder='{"text_list_length": 5}'
        rows={4}
        className="font-mono text-xs"
        aria-invalid={displayedError ? true : undefined}
        aria-describedby={displayedError ? 'metadata-error' : undefined}
      />
      {displayedError ? (
        <p id="metadata-error" role="alert" className="text-xs text-destructive">
          {displayedError}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {helpText ?? 'JSON object for additional question configuration.'}
        </p>
      )}
    </div>
  );
};

type QuestionContentProps = Pick<QuestionLibraryEditorProps, 'draft' | 'saving' | 'onInputChange' | 'helperCopy'> & {
  hasLearnerLabels: boolean;
};

/** Keyed by question so disclosure and preview choices never leak between drafts. */
const QuestionContent: React.FC<QuestionContentProps> = ({ draft, saving, onInputChange, helperCopy, hasLearnerLabels }) => {
  const citations = draft.citations ?? [];
  const [showOptional, setShowOptional] = React.useState(Boolean(draft.header || draft.subtext));
  const [narrowPreview, setNarrowPreview] = React.useState(false);
  // Personalized wording replaces Question content, so only header and subtext can reference answers there.
  const personalized = (draft.metadata as { ai_generation?: { enabled?: unknown } } | undefined)?.ai_generation?.enabled === true;
  const referencedKeys = extractAnswerReferenceKeys([draft.header, personalized ? null : draft.text, draft.subtext]);
  const ignoredTextReferences = personalized && extractAnswerReferenceKeys([draft.text]).length > 0;
  // Double-brace text that is not a valid reference is shown to learners as written.
  const invalidReferences = Array.from(new Set([draft.header, draft.text, draft.subtext]
    .flatMap((value) => (value ?? '').match(/\{\{[^{}\n]*\}\}/g) ?? [])
    .filter((candidate) => !ANSWER_REFERENCE_PATTERN.test(candidate))));
  const field = (name: 'text' | 'header' | 'subtext', label: string, previewRole: 'prompt' | 'compact' | 'body') => (
    <div className="min-w-0 space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <QuestionMarkdownEditor
        id={name}
        label={label}
        value={draft[name] ?? ''}
        onChange={(value) => onInputChange(name, value)}
        markdown
        parentPreview
        previewRole={previewRole}
        citations={citations}
        disabled={saving}
        rows={name === 'text' ? 12 : 3}
        answerReferences
      />
      {helperCopy?.fields?.[name] ? <p className="text-xs text-muted-foreground">{helperCopy.fields[name]}</p> : null}
    </div>
  );

  return (
    <section aria-label="Question content workspace" className="grid min-w-0 gap-6 xl:grid-cols-2">
      <div className="min-w-0 space-y-4">
        {field('text', 'Question content', 'prompt')}
        <div className="min-w-0 rounded-xl border border-border/40 p-4">
          <Button
            type="button"
            variant="ghost"
            size="auto"
            className="flex w-full items-center justify-between gap-2 text-left"
            aria-expanded={showOptional}
            aria-controls="optional-question-fields"
            onClick={() => setShowOptional((open) => !open)}
          >
            <span>Header &amp; subtext (optional)</span>
            <ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 ${showOptional ? 'rotate-180' : ''}`} />
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Use these for a separate heading or supporting instructions. Existing values stay in place.
          </p>
          <div id="optional-question-fields" hidden={!showOptional} className="mt-4 space-y-4">
            {field('header', 'Header', 'compact')}
            {field('subtext', 'Subtext', 'body')}
          </div>
        </div>
      </div>
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Learner preview</h3>
          <div role="group" aria-label="Learner preview width" className="flex gap-1">
            {(['wide', 'mobile'] as const).map((width) => (
              <Button type="button" key={width} size="auto" variant={narrowPreview === (width === 'mobile') ? 'secondary' : 'ghost'}
                className="px-3 py-1 text-xs" aria-pressed={narrowPreview === (width === 'mobile')}
                onClick={() => setNarrowPreview(width === 'mobile')}>
                {width === 'wide' ? 'Wide' : 'Mobile'}
              </Button>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Current unsaved text and references. Answer controls and media appear in the module.
          Callback values and earlier answers are filled when a learner reaches the question.
        </p>
        {referencedKeys.length ? (
          <p className="text-xs text-muted-foreground" data-testid="answer-reference-summary">
            Shows the learner&apos;s saved answer to {referencedKeys.join(', ')}.
            {draft.key && referencedKeys.includes(draft.key) ? ' This question references its own key; reference an earlier question instead.' : ''}
          </p>
        ) : null}
        {invalidReferences.length ? (
          <p className="text-xs text-muted-foreground" data-testid="answer-reference-invalid-note">
            {invalidReferences.join(', ')} will show as written. Earlier-answer keys use letters, numbers, _ . : and -, with an optional |fallback.
          </p>
        ) : null}
        {ignoredTextReferences ? (
          <p className="text-xs text-muted-foreground" data-testid="answer-reference-personalized-note">
            Personalized wording replaces Question content, so earlier-answer references there are not shown. Use Header or Subtext.
          </p>
        ) : null}
        <AnswerReferenceContext.Provider value={ANSWER_REFERENCE_PREVIEW}>
        <div role="region" aria-label="Unsaved learner text preview"
          className={`min-w-0 space-y-4 overflow-x-auto rounded-xl border bg-white p-4 ${narrowPreview ? 'mx-auto w-full max-w-[23.4375rem]' : ''}`}>
          {draft.header ? <div className="text-sm font-semibold uppercase"><QuestionText text={draft.header} inline citations={citations} answerReferences /></div> : null}
          {draft.text ? <QuestionPrompt text={draft.text} citations={citations} /> : <p className="text-muted-foreground">Your question content will appear here.</p>}
          <QuestionText text={draft.subtext} citations={citations} className="text-base" answerReferences />
          {hasLearnerLabels && (draft.low_text || draft.high_text) ? (
            <div className="flex flex-wrap justify-between gap-4 text-sm">
              <QuestionText text={draft.low_text} inline citations={citations} />
              <QuestionText text={draft.high_text} inline citations={citations} />
            </div>
          ) : null}
          <CitationList citations={citations} usedIndices={extractQuestionCitationIndices([
            draft.text, draft.subtext, draft.header,
            ...(hasLearnerLabels ? [draft.low_text, draft.high_text] : []),
          ])} className="px-0 pb-0" />
        </div>
        </AnswerReferenceContext.Provider>
      </div>
    </section>
  );
};

// Matches the primary navigation, which is fixed to the bottom of the screen below Tailwind's lg breakpoint.
const BOTTOM_NAVIGATION_QUERY = '(max-width: 1023.98px)';

export const QuestionLibraryEditor: React.FC<QuestionLibraryEditorProps> = ({
  helperCopy,
  draft,
  keyError,
  selected,
  attachmentModuleLinks,
  selectedQuestionTypeValue,
  questionTypeAvailabilityError,
  onSelectQuestionType,
  onInputChange,
  metadataValidationError,
  metadataParseError,
  onMetadataValidationChange,
  optionValidationError,
  onMediaAssetChange,
  restrictedMediaTypes,
  saving,
  multipleSelectLimitInputs,
  multipleSelectLimitValidation,
  onMultipleSelectLimitInputChange,
  optionList,
  onAddOption,
  onUpdateOptionField,
  onUpdateOptionValue,
  onUpdateOptionFlag,
  onRemoveOption,
  attachableFlows,
  filteredAttachableFlows,
  attachFlowIds,
  attachFlowRequiredById,
  onToggleAttachFlow,
  onToggleAttachFlowRequired,
  onRemoveAttachFlow,
  flowSearch,
  onFlowSearchChange,
  flows,
  hasPreviewResult,
  diffPreview,
  diffPreviewDetails,
  answerReferenceWarnings = [],
  summaryItems,
  formatFieldLabel,
  hasChanges,
  onPreview,
  onSave,
  onDiscard,
  onLocalDirtyChange,
  onOpenRadarDefinition,
  roleplayPersonas = [],
  roleplayVoiceOptions = DEFAULT_ROLEPLAY_VOICE_OPTIONS,
}) => {
  const citations = draft.citations ?? [];
  const isRoleplay = selectedQuestionTypeValue === 'roleplay';
  const voicePresentationError = validateVoicePresentation(draft.metadata, selectedQuestionTypeValue, optionList);
  const isReveal = normalizeQuestionType(selectedQuestionTypeValue) === 'multiple_reveal';
  const generationValue = draft.metadata?.ai_generation;
  const generationConfig = generationValue && typeof generationValue === 'object' && !Array.isArray(generationValue)
    ? generationValue as Record<string, unknown>
    : {};
  const generationHasCorrectness = hasQuestionGenerationCorrectness(draft.metadata, optionList);
  const generationCorrectnessError = generationConfig.enabled === true && generationHasCorrectness;
  const updateGenerationConfig = (changes: Record<string, unknown>) => onInputChange('metadata', {
    ...draft.metadata,
    ai_generation: { ...generationConfig, ...changes },
  });
  const evaluationValue = draft.metadata?.ai_evaluation;
  const evaluationConfig = evaluationValue && typeof evaluationValue === 'object' && !Array.isArray(evaluationValue)
    ? evaluationValue as Record<string, unknown>
    : {};
  const updateEvaluationConfig = (changes: Record<string, unknown>) => onInputChange('metadata', {
    ...draft.metadata,
    ai_evaluation: { ...evaluationConfig, ...changes },
  });
  // These fields hold configuration/private prompts on other question types.
  const hasLearnerLabels = ['rating', 'boolean', 'horizontal_drag_drop'].includes(
    normalizeQuestionType(selectedQuestionTypeValue),
  );
  const defaultRoleplayPersonaKey = roleplayPersonas[0]?.key ?? '';
  const [roleplayFormValues, setRoleplayFormValues] = React.useState<RoleplayMetadataFormValues>(
    () => roleplayMetadataToFormValues(draft.metadata, defaultRoleplayPersonaKey),
  );

  React.useEffect(() => {
    if (!isRoleplay) return;
    setRoleplayFormValues(roleplayMetadataToFormValues(
      draft.metadata,
      defaultRoleplayPersonaKey,
    ));
  }, [defaultRoleplayPersonaKey, draft.metadata, isRoleplay]);

  const updateRoleplayForm = <Key extends keyof RoleplayMetadataFormValues,>(
    key: Key,
    value: RoleplayMetadataFormValues[Key],
  ) => {
    setRoleplayFormValues((current) => ({ ...current, [key]: value }));
  };
  const roleplayFormHasUnpulledChanges = isRoleplay
    && stableStringify(mergeRoleplayFormValuesIntoMetadata(draft.metadata, roleplayFormValues))
      !== stableStringify(draft.metadata ?? {});
  const buildScorecardSuggestionRequest = (): RoleplayScorecardSuggestionRequest => ({
    title: draft.header ?? '',
    situation: draft.text ?? '',
    learner_role: draft.low_text ?? '',
    learning_objectives: roleplayFormValues.learningObjectives,
    win_condition_prompt: roleplayFormValues.winConditionPrompt,
    actor_prompt: roleplayFormValues.actorPrompt,
  });
  const pullRoleplayFormIntoRawMetadata = () => {
    onInputChange(
      'metadata',
      mergeRoleplayFormValuesIntoMetadata(draft.metadata, roleplayFormValues),
    );
  };

  const handleAddCitation = () => {
    onInputChange('citations', [...citations, '']);
  };

  const handleUpdateCitation = (index: number, value: string) => {
    const next = [...citations];
    next[index] = value;
    onInputChange('citations', next);
  };

  const handleRemoveCitation = (index: number) => {
    const next = citations.filter((_, currentIndex) => currentIndex !== index);
    onInputChange('citations', next);
  };

  // Below lg the save bar is fixed above the bottom navigation; show it only while the editor is on screen.
  const editorRootRef = React.useRef<HTMLDivElement>(null);
  const [editorOnScreen, setEditorOnScreen] = React.useState(true);
  React.useEffect(() => {
    const node = editorRootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setEditorOnScreen(entry.isIntersecting),
      // The bottom 128px (8rem) of the viewport is the bar and the navigation; the editor must show above them.
      { rootMargin: '0px 0px -128px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const [bottomNavShown, setBottomNavShown] = React.useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(BOTTOM_NAVIGATION_QUERY).matches,
  );
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(BOTTOM_NAVIGATION_QUERY);
    const handleChange = () => setBottomNavShown(query.matches);
    handleChange();
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);
  const hasLocalEdits = roleplayFormHasUnpulledChanges || !!metadataParseError;
  const hasUnsavedEdits = hasChanges || hasLocalEdits;
  React.useEffect(() => {
    onLocalDirtyChange?.(hasLocalEdits);
  }, [hasLocalEdits, onLocalDirtyChange]);
  React.useEffect(() => () => onLocalDirtyChange?.(false), [onLocalDirtyChange]);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [reviewLoading, setReviewLoading] = React.useState(false);
  // Confirming is only allowed once the diff for this exact review request has come back.
  const [previewReady, setPreviewReady] = React.useState(false);
  const reviewRequestRef = React.useRef(0);
  const canSave = !(
    saving ||
    !!questionTypeAvailabilityError ||
    !hasChanges ||
    !!keyError ||
    !!metadataValidationError ||
    !!voicePresentationError ||
    !!optionValidationError ||
    generationCorrectnessError ||
    roleplayFormHasUnpulledChanges
  );
  const handleOpenReview = async () => {
    const requestId = reviewRequestRef.current + 1;
    reviewRequestRef.current = requestId;
    setPreviewReady(false);
    setReviewLoading(true);
    setReviewOpen(true);
    try {
      const result = await onPreview();
      if (reviewRequestRef.current !== requestId) {
        return;
      }
      if (result === false) {
        setReviewOpen(false);
      } else {
        setPreviewReady(true);
      }
    } catch {
      if (reviewRequestRef.current === requestId) {
        setReviewOpen(false);
      }
    } finally {
      if (reviewRequestRef.current === requestId) {
        setReviewLoading(false);
      }
    }
  };
  const closeReview = () => {
    reviewRequestRef.current += 1;
    setReviewOpen(false);
    setReviewLoading(false);
    setPreviewReady(false);
  };
  const handleConfirmSave = async () => {
    if (!previewReady) {
      return;
    }
    await onSave();
    closeReview();
  };

  const saveBarPill = (
    <div
      className={`pointer-events-auto flex items-center gap-3 rounded-full py-1.5 pl-5 pr-1.5 text-sm shadow-lg transition-colors ${
        hasUnsavedEdits
          ? 'bg-foundation-base-darkbase-dark-500 text-white'
          : 'border border-foundation-calm-blue-200 bg-white text-muted-foreground'
      }`}
      aria-live="polite"
    >
      {hasUnsavedEdits ? (
        <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
      ) : null}
      <span>{saving ? 'Saving…' : hasUnsavedEdits ? 'Unsaved changes' : 'No unsaved changes'}</span>
      {hasUnsavedEdits && onDiscard ? (
        <Button
          type="button"
          variant="ghost"
          size="auto"
          className="px-3 py-1.5 text-white hover:text-white/80"
          onClick={onDiscard}
          disabled={saving}
        >
          Discard
        </Button>
      ) : null}
      <Button
        type="button"
        size="auto"
        className={`px-4 py-1.5 disabled:bg-slate-200 disabled:text-slate-500 disabled:opacity-100 ${hasUnsavedEdits ? 'bg-white text-foundation-base-darkbase-dark-700 hover:bg-white/90' : ''}`}
        onClick={handleOpenReview}
        disabled={!canSave}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

  return (
    <div ref={editorRootRef} className="space-y-4">
      {helperCopy?.general ? (
        <p className="text-sm text-muted-foreground">{helperCopy.general}</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="min-w-0 space-y-1 md:col-span-1">
          <Label className="block text-xs uppercase text-muted-foreground" htmlFor="key">
            Key
          </Label>
          <Input
            id="key"
            value={draft.key}
            onChange={(event) => onInputChange('key', event.target.value)}
            placeholder="Unique key"
            disabled={!!selected}
            className="disabled:bg-white"
          />
          {keyError ? <p className="break-words text-xs text-destructive">{keyError}</p> : null}
        </div>
        <div className="space-y-1 md:col-span-1">
          <Label className="block text-xs uppercase text-muted-foreground" htmlFor="question-type-select">
            Question Type
          </Label>
          <Select value={selectedQuestionTypeValue} onValueChange={onSelectQuestionType}>
            <SelectTrigger
              id="question-type-select"
              className="w-full"
              aria-invalid={!!questionTypeAvailabilityError}
              aria-describedby={questionTypeAvailabilityError ? 'question-type-availability' : undefined}
            >
              <SelectValue placeholder="Select question type" />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {questionTypeAvailabilityError ? (
            <p id="question-type-availability" role="status" className="text-xs text-destructive">
              {questionTypeAvailabilityError}
            </p>
          ) : null}
        </div>
        <div className="space-y-1 md:col-span-1">
          <Label className="block text-xs uppercase text-muted-foreground" htmlFor="category">
            Category
          </Label>
          <Input
            id="category"
            value={draft.category}
            onChange={(event) => onInputChange('category', event.target.value)}
            placeholder="Category"
          />
        </div>
        <div className="md:col-span-3 flex justify-center">
          <MediaAssetPicker
            value={draft.media ?? null}
            onChange={onMediaAssetChange}
            mediaTypes={restrictedMediaTypes}
            label="Media Asset"
            description="Attach an existing media asset. Leave empty to remove the current asset."
            disabled={saving}
          />
        </div>
      </div>

      {!isRoleplay ? (
        <p className="text-xs text-muted-foreground">
          Write the complete question in Question content. Use Markdown headings, text sizes, emphasis, lists, links, quotes and tables.
          Write normally or use the formatting controls. Citations use {'{1}'} markers.
        </p>
      ) : null}

      {!isRoleplay ? (
        <QuestionContent key={selected?.id ?? 'new-question'} draft={draft} saving={saving}
          onInputChange={onInputChange} helperCopy={helperCopy} hasLearnerLabels={hasLearnerLabels} />
      ) : null}

      <div className={`grid w-full min-w-0 gap-3 md:grid-cols-2 ${isRoleplay ? 'md:max-w-[37.5rem]' : ''}`}>
        {isRoleplay ? (
          <>
            <div className="min-w-0 space-y-1 md:col-span-2">
              <Label htmlFor="text">Situation</Label>
              <QuestionMarkdownEditor
                key={`text-${selected?.id ?? 'new'}`}
                id="text"
                label="Text"
                value={draft.text}
                onChange={(value) => onInputChange('text', value)}
                markdown={false}
                previewRole="prompt"
                citations={citations}
                disabled={saving}
                rows={6}
              />
              {helperCopy?.fields?.text ? (
                <p className="text-xs text-muted-foreground">{helperCopy.fields.text}</p>
              ) : null}
            </div>
            <div className="min-w-0 space-y-1">
              <Label htmlFor="header">Title</Label>
              <QuestionMarkdownEditor
                key={`header-${selected?.id ?? 'new'}`}
                id="header"
                label="Header"
                value={draft.header ?? ''}
                onChange={(value) => onInputChange('header', value)}
                markdown={false}
                previewRole="compact"
                citations={citations}
                disabled={saving}
                rows={3}
              />
              {helperCopy?.fields?.header ? (
                <p className="text-xs text-muted-foreground">{helperCopy.fields.header}</p>
              ) : null}
            </div>
            <div className="min-w-0 space-y-1">
              <Label htmlFor="subtext">Subtitle (optional)</Label>
              <QuestionMarkdownEditor
                key={`subtext-${selected?.id ?? 'new'}`}
                id="subtext"
                label="Subtext"
                value={draft.subtext ?? ''}
                onChange={(value) => onInputChange('subtext', value)}
                markdown={false}
                previewRole="body"
                citations={citations}
                disabled={saving}
                rows={3}
              />
              {helperCopy?.fields?.subtext ? (
                <p className="text-xs text-muted-foreground">{helperCopy.fields.subtext}</p>
              ) : null}
            </div>
          </>
        ) : null}
        <div className="min-w-0 space-y-1">
          <Label htmlFor="low-text">{isRoleplay ? 'Learner role' : 'Low Text'}</Label>
          <QuestionMarkdownEditor
            key={`low-text-${selected?.id ?? 'new'}`}
            id="low-text"
            label="Low Text"
            value={draft.low_text ?? ''}
            onChange={(value) => onInputChange('low_text', value)}
            markdown={hasLearnerLabels}
            previewRole="compact"
            citations={citations}
            disabled={saving}
            rows={3}
          />
          {helperCopy?.fields?.low_text ? (
            <p className="text-xs text-muted-foreground">{helperCopy.fields.low_text}</p>
          ) : null}
        </div>
        <div className="min-w-0 space-y-1">
          <Label htmlFor="high-text">{isRoleplay ? 'Fixed opening (optional)' : 'High Text'}</Label>
          <QuestionMarkdownEditor
            key={`high-text-${selected?.id ?? 'new'}`}
            id="high-text"
            label="High Text"
            value={draft.high_text ?? ''}
            onChange={(value) => onInputChange('high_text', value)}
            markdown={hasLearnerLabels}
            previewRole="compact"
            citations={citations}
            disabled={saving}
            rows={3}
          />
          {helperCopy?.fields?.high_text ? (
            <p className="text-xs text-muted-foreground">{helperCopy.fields.high_text}</p>
          ) : null}
        </div>
      </div>

      {selectedQuestionTypeValue === 'multiple_select' ? (
        <div className="grid gap-3 md:grid-cols-2 md:max-w-[37.5rem]">
          <div className="space-y-1">
            <Label htmlFor="multiple-select-min">Minimum selections</Label>
            <Input
              id="multiple-select-min"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={multipleSelectLimitInputs.minSelections}
              onChange={(event) =>
                onMultipleSelectLimitInputChange('minSelections', event.target.value)
              }
              placeholder="Optional"
            />
            {multipleSelectLimitValidation.minError ? (
              <p className="text-xs text-destructive">{multipleSelectLimitValidation.minError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Leave blank to allow any minimum selection count.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="multiple-select-max">Maximum selections</Label>
            <Input
              id="multiple-select-max"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={multipleSelectLimitInputs.maxSelections}
              onChange={(event) =>
                onMultipleSelectLimitInputChange('maxSelections', event.target.value)
              }
              placeholder="Optional"
            />
            {multipleSelectLimitValidation.maxError ? (
              <p className="text-xs text-destructive">{multipleSelectLimitValidation.maxError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Leave blank to allow any maximum selection count.
              </p>
            )}
          </div>
        </div>
      ) : null}

      {OPTION_REQUIRED_TYPES.has(selectedQuestionTypeValue) && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase text-muted-foreground">Options</Label>
            <Button variant="secondary" size="auto" onClick={onAddOption}>
              Add Option
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {helperCopy?.options?.general
              ? helperCopy.options.general
              : 'Provide the content for each option. Mark the correct option(s) when applicable.'}
          </p>
          {optionValidationError ? (
            <p id="option-label-error" role="alert" className="text-xs text-destructive">
              {optionValidationError}
            </p>
          ) : null}
          {optionList.map((option, index) => (
            <div
              key={option.id ?? index}
              className="space-y-3 rounded-lg border border-border/40 bg-white/5 p-4"
            >
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor={`option-front-text-${index}`}>Front Text</Label>
                  {isReveal ? <QuestionMarkdownEditor
                    id={`option-front-text-${index}`}
                    label={`Card ${index + 1} front`}
                    value={option.front_text}
                    onChange={(value) => onUpdateOptionField(index, { front_text: value })}
                    markdown
                    previewRole="reveal-front"
                    placeholder={`Option ${index + 1}`}
                    rows={3}
                    invalid={option.front_text.trim().length === 0}
                    describedBy={option.front_text.trim().length === 0 && optionValidationError ? 'option-label-error' : undefined}
                  /> : <Textarea
                    id={`option-front-text-${index}`}
                    value={option.front_text}
                    onChange={(event) => onUpdateOptionField(index, { front_text: event.target.value })}
                    placeholder={`Option ${index + 1}`}
                    rows={3}
                    aria-invalid={option.front_text.trim().length === 0 ? true : undefined}
                    aria-describedby={
                      option.front_text.trim().length === 0 && optionValidationError
                        ? 'option-label-error'
                        : undefined
                    }
                  />}
                  {helperCopy?.options?.front ? (
                    <p className="text-[11px] text-muted-foreground">{helperCopy.options.front}</p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`option-back-text-${index}`}>Back Text</Label>
                  {isReveal ? <QuestionMarkdownEditor
                    id={`option-back-text-${index}`}
                    label={`Card ${index + 1} back`}
                    value={option.back_text}
                    onChange={(value) => onUpdateOptionField(index, { back_text: value })}
                    markdown
                    previewRole="reveal-back"
                    placeholder="Back text (optional)"
                    rows={3}
                  /> : <Textarea
                    id={`option-back-text-${index}`}
                    value={option.back_text}
                    onChange={(event) => onUpdateOptionField(index, { back_text: event.target.value })}
                    placeholder="Back text (optional)"
                    rows={3}
                  />}
                  {helperCopy?.options?.back ? (
                    <p className="text-[11px] text-muted-foreground">{helperCopy.options.back}</p>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="space-y-1">
                  <Label>Value</Label>
                  <Input
                    type="number"
                    value={option.value}
                    onChange={(event) => onUpdateOptionValue(index, event.target.value)}
                    className="w-24"
                  />
                  {helperCopy?.options?.value ? (
                    <p className="text-[11px] text-muted-foreground">{helperCopy.options.value}</p>
                  ) : null}
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={option.is_correct}
                    onCheckedChange={(checked) => onUpdateOptionFlag(index, 'is_correct', checked)}
                  />
                  Correct
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={option.is_other}
                    onCheckedChange={(checked) => onUpdateOptionFlag(index, 'is_other', checked)}
                  />
                  Allow "Other"
                </label>
                <Button
                  variant="ghost"
                  size="auto"
                  onClick={() => onRemoveOption(index)}
                  disabled={optionList.length === 1}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected?.radar_memberships?.length ? (
        <div className="space-y-3 rounded-3xl border border-border/40 bg-white/40 p-4">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Radar profiles</Label>
            <p className="text-xs text-muted-foreground/80">
              This rating question contributes to the following radar chart axes.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.radar_memberships.map((membership) => (
              <Button
                key={`${membership.definition_key}-${membership.axis_key}`}
                type="button"
                variant="secondary"
                size="auto"
                onClick={() => onOpenRadarDefinition?.(membership.definition_key)}
              >
                {membership.definition_name} · {membership.axis_label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {isRoleplay ? (
        <div className="space-y-3 rounded-3xl border border-border/40 bg-white/40 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Label className="text-xs uppercase text-muted-foreground" htmlFor="metadata">
                Raw metadata (Required)
              </Label>
              <p className="text-xs text-muted-foreground/80">
                Edit any JSON key here. Pulling the form only replaces the roleplay keys it owns.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="auto"
              onClick={pullRoleplayFormIntoRawMetadata}
              disabled={!roleplayFormHasUnpulledChanges || !!metadataParseError}
              data-testid="roleplay-pull-form-metadata"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Pull form values into raw
            </Button>
          </div>
          <MetadataEditor
            key={selected?.id ?? 'new-question'}
            value={draft.metadata}
            onChange={(next) => onInputChange('metadata', next)}
            validationError={metadataValidationError}
            onValidationChange={onMetadataValidationChange}
            helpText="Raw JSON remains canonical. Unknown keys are preserved when structured values are pulled in."
          />
          {roleplayFormHasUnpulledChanges ? (
            <p className="text-xs font-medium text-amber-700" role="status">
              Structured form changes are staged. Pull them into raw metadata before previewing or saving.
            </p>
          ) : null}
        </div>
      ) : null}

      {isRoleplay ? (
        <div className="space-y-4 rounded-3xl border border-border/40 bg-white/40 p-4">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Roleplay configuration</Label>
            <p className="text-xs text-muted-foreground/80">
              These controls write the roleplay-only values into question metadata.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="roleplay-persona">Actor portrait</Label>
              <Select
                value={roleplayFormValues.personaKey}
                onValueChange={(value) => updateRoleplayForm('personaKey', value)}
              >
                <SelectTrigger id="roleplay-persona"><SelectValue placeholder="Select persona" /></SelectTrigger>
                <SelectContent>
                  {roleplayPersonas.map((persona) => (
                    <SelectItem key={persona.key} value={persona.key}>{persona.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="roleplay-expression">Starting expression</Label>
              <Select
                value={roleplayFormValues.startingExpression}
                onValueChange={(value) => updateRoleplayForm('startingExpression', value)}
              >
                <SelectTrigger id="roleplay-expression"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLEPLAY_EXPRESSIONS.map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="roleplay-voice">Actor voice</Label>
              <Select
                value={roleplayFormValues.realtimeVoice}
                onValueChange={(value) => updateRoleplayForm('realtimeVoice', value)}
              >
                <SelectTrigger id="roleplay-voice"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!roleplayVoiceOptions.some(
                    (option) => option.alias === roleplayFormValues.realtimeVoice,
                  ) ? (
                    <SelectItem value={roleplayFormValues.realtimeVoice}>
                      Unavailable voice · {roleplayFormValues.realtimeVoice}
                    </SelectItem>
                  ) : null}
                  {roleplayVoiceOptions.map((option) => (
                    <SelectItem key={option.alias} value={option.alias}>
                      {option.name} · {option.provider === 'openai' ? 'OpenAI' : 'ElevenLabs'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="roleplay-duration">Duration (seconds)</Label>
                <Input
                  id="roleplay-duration"
                  type="number"
                  min={60}
                  max={3600}
                  value={roleplayFormValues.maxActiveSeconds}
                  onChange={(event) => updateRoleplayForm('maxActiveSeconds', event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="roleplay-turns">Learner turns</Label>
                <Input
                  id="roleplay-turns"
                  type="number"
                  min={1}
                  max={50}
                  value={roleplayFormValues.maxLearnerTurns}
                  onChange={(event) => updateRoleplayForm('maxLearnerTurns', event.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="roleplay-objectives">Learning objectives</Label>
            <Textarea
              id="roleplay-objectives"
              rows={4}
              value={roleplayFormValues.learningObjectives.join('\n')}
              onChange={(event) => updateRoleplayForm(
                'learningObjectives',
                event.target.value.split('\n').map((value) => value.trim()).filter(Boolean),
              )}
              placeholder="One objective per line"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="roleplay-actor-prompt">Private actor prompt</Label>
            <Textarea
              id="roleplay-actor-prompt"
              rows={8}
              value={roleplayFormValues.actorPrompt}
              onChange={(event) => updateRoleplayForm('actorPrompt', event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="roleplay-win-prompt">Private win-condition prompt</Label>
            <Textarea
              id="roleplay-win-prompt"
              rows={6}
              value={roleplayFormValues.winConditionPrompt}
              onChange={(event) => updateRoleplayForm('winConditionPrompt', event.target.value)}
            />
          </div>
          <RoleplayScorecardEditor
            key={selected?.id ?? 'new-question'}
            categories={roleplayFormValues.scorecardCategories}
            onChange={(next) => updateRoleplayForm('scorecardCategories', next)}
            buildSuggestionRequest={buildScorecardSuggestionRequest}
          />
        </div>
      ) : (
      <div className="space-y-3 rounded-3xl border border-border/40 bg-white/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Citations</Label>
            <p className="text-xs text-muted-foreground/80">
              Use {'{n}'} placeholders (e.g., {'{1}'}, {'{2}'}) in your text or subtext to reference each entry.
            </p>
          </div>
        </div>
        <div>
          <Button type="button" variant="secondary" size="auto" onClick={handleAddCitation}>
            Add citation
          </Button>
        </div>
        {citations.length === 0 ? null : (
          <div className="space-y-3">
            {citations.map((citation, index) => {
              const fieldId = `citation-${index}`;
              return (
                <div
                  key={fieldId}
                  className="flex flex-col gap-2 md:flex-row md:items-start md:gap-3"
                >
                  <Label
                    htmlFor={fieldId}
                    className="text-xs font-semibold uppercase text-muted-foreground md:pt-2"
                  >
                    Citation {index + 1}
                  </Label>
                  <div className="flex flex-1 items-start gap-2">
                    <Textarea
                      id={fieldId}
                      value={citation}
                      onChange={(event) => handleUpdateCitation(index, event.target.value)}
                      placeholder="Source, study, or link"
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove Citation ${index + 1}`}
                      onClick={() => handleRemoveCitation(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <VoicePresentationEditor
        metadata={draft.metadata}
        questionType={selectedQuestionTypeValue}
        questionText={draft.text}
        options={optionList}
        disabled={saving || !!metadataParseError}
        error={voicePresentationError}
        onChange={(next) => onInputChange('metadata', next)}
      />

      {selectedQuestionTypeValue !== 'roleplay' ? (
        <div className="space-y-3 rounded-3xl border border-border/40 bg-white/40 p-4">
          <Label className="text-xs uppercase text-muted-foreground" htmlFor="metadata">
            Metadata {selectedQuestionTypeValue === 'callback_summary' ? '(Required)' : '(Optional)'}
          </Label>
          <MetadataEditor
            key={selected?.id ?? 'new-question'}
            value={draft.metadata}
            onChange={(next) => onInputChange('metadata', next)}
            validationError={metadataValidationError}
            onValidationChange={onMetadataValidationChange}
            helpText={helperCopy?.metadata}
          />
        </div>
      ) : null}

      {supportsQuestionGeneration(selectedQuestionTypeValue) && (
        <section className="space-y-4 rounded-3xl border border-border/40 bg-white/60 p-4" aria-label="Question personalization">
          <div className="flex items-start gap-3">
            <Checkbox id="ai-generation-enabled" checked={generationConfig.enabled === true}
              disabled={saving || Boolean(metadataParseError) || (generationHasCorrectness && generationConfig.enabled !== true)}
              onCheckedChange={(checked) => updateGenerationConfig({ enabled: checked === true, ...(checked === true && !generationConfig.prompt ? { prompt: '' } : {}) })} />
            <div className="space-y-1">
              <Label htmlFor="ai-generation-enabled">Personalize the question</Label>
              <p className="text-xs text-muted-foreground">Uses this learner’s previous answers to personalize the question. The question text above is still required. Available in modules outside quizzes.</p>
            </div>
          </div>
          {generationHasCorrectness && (
            <p role={generationCorrectnessError ? 'alert' : undefined} className={generationCorrectnessError ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
              Personalized questions cannot have a fixed correct answer. Clear the correct answer settings to use personalization.
            </p>
          )}
          {generationConfig.enabled === true && (
            <div className="space-y-2">
              <Label htmlFor="ai-generation-prompt">Personalization instructions</Label>
              <Textarea id="ai-generation-prompt" rows={5}
                value={typeof generationConfig.prompt === 'string' ? generationConfig.prompt : ''}
                disabled={saving || Boolean(metadataParseError)}
                onChange={(event) => updateGenerationConfig({ prompt: event.target.value })}
                placeholder="For example: Ask about a next step connected to a challenge the learner has described."
                aria-describedby="ai-generation-prompt-help" />
              <p id="ai-generation-prompt-help" className="text-xs text-muted-foreground">These instructions are private. Describe how to tailor the question while keeping its answer format and options unchanged.</p>
            </div>
          )}
        </section>
      )}

      {supportsAnswerEvaluation(selectedQuestionTypeValue) && (
        <section className="space-y-4 rounded-3xl border border-border/40 bg-white/60 p-4" aria-label="Follow-up settings">
          <div className="flex items-start gap-3">
            <Checkbox id="ai-evaluation-enabled" checked={evaluationConfig.enabled === true}
              disabled={Boolean(metadataParseError)}
              onCheckedChange={(checked) => updateEvaluationConfig({ enabled: checked === true, ...(checked === true && !evaluationConfig.criteria ? { criteria: '' } : {}) })} />
            <div className="space-y-1">
              <Label htmlFor="ai-evaluation-enabled">Ask follow-up questions when needed</Label>
              <p className="text-xs text-muted-foreground">Ask for more detail only when an answer needs it, using the criteria below. Available in modules outside quizzes.</p>
            </div>
          </div>
          {evaluationConfig.enabled === true && <>
            <div className="space-y-2">
              <Label htmlFor="ai-evaluation-criteria">Criteria for a sufficient answer</Label>
              <Textarea id="ai-evaluation-criteria" rows={5}
                value={typeof evaluationConfig.criteria === 'string' ? evaluationConfig.criteria : ''}
                disabled={Boolean(metadataParseError)}
                onChange={(event) => updateEvaluationConfig({ criteria: event.target.value })}
                placeholder="For example: Describe a specific situation, explain your response, and identify one thing you would try next time."
                aria-describedby="ai-evaluation-criteria-help" />
              <p id="ai-evaluation-criteria-help" className="text-xs text-muted-foreground">These instructions are private. Describe the depth or evidence you need without requiring a particular belief or invented details.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ai-evaluation-limit">Maximum follow-up questions</Label>
              <Input id="ai-evaluation-limit" type="number" min={1} max={5} step={1} className="max-w-24"
                value={typeof evaluationConfig.max_follow_ups === 'number' ? evaluationConfig.max_follow_ups : ''}
                placeholder="3"
                disabled={Boolean(metadataParseError)}
                onChange={(event) => {
                  const config = { ...evaluationConfig };
                  if (event.target.value === '') delete config.max_follow_ups;
                  else config.max_follow_ups = Number(event.target.value);
                  onInputChange('metadata', { ...draft.metadata, ai_evaluation: config });
                }} />
              <p className="text-xs text-muted-foreground">Choose 1–5 (default 3). At the limit, the learner can continue with their reflection saved.</p>
            </div>
          </>}
        </section>
      )}

      <div className="space-y-3 rounded-3xl bg-white p-4">
        <div className="flex flex-col gap-1">
          <Label className="text-xs uppercase text-muted-foreground">Attach To Modules (Optional)</Label>
          <p className="text-xs text-muted-foreground">
            Select modules to automatically add this question after saving. Existing attachments remain unchanged.
          </p>
        </div>
        <div role="group" aria-label="Currently attached modules" className="space-y-2 rounded-lg border border-border/40 bg-white/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Currently attached</p>
          {selected?.flows?.length ? (
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              {selected.flows.map((flow) => (
                <li key={flow.flow_id}>
                  <Link to={attachmentModuleLinks?.[flow.flow_id] ?? moduleBuilderHref({ flowId: flow.flow_id })} title="Open in Module Builder" className="underline-offset-2 text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                    {flow.flow_name}
                  </Link>{' '}
                  <span className="text-[11px]">
                    (order {flow.order + 1}, {flow.is_required ? 'required' : 'optional'})
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground/80">Not currently attached to any modules.</p>
          )}
        </div>
        {attachableFlows.length > 0 ? (
          <div className="space-y-2 rounded-lg border border-border/40 bg-white/5 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <DropdownMenu
                onOpenChange={(open) => {
                  if (!open) {
                    onFlowSearchChange('');
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="auto" className="flex items-center gap-1">
                    Add modules
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 p-0">
                  <div className="p-2">
                    <Input
                      value={flowSearch}
                      onChange={(event) => onFlowSearchChange(event.target.value)}
                      placeholder="Search modules..."
                      autoFocus
                    />
                  </div>
                  <div className="max-h-64 overflow-auto px-1 pb-1">
                    {filteredAttachableFlows.length > 0 ? (
                      filteredAttachableFlows.map((flow) => {
                        const id = String(flow.id);
                        return (
                          <DropdownMenuCheckboxItem
                            key={flow.id}
                            checked={attachFlowIds.includes(id)}
                            onCheckedChange={(checked) => onToggleAttachFlow(flow.id, checked)}
                            onSelect={(event) => event.preventDefault()}
                          >
                            {flow.name}
                          </DropdownMenuCheckboxItem>
                        );
                      })
                    ) : (
                      <p className="px-3 py-2 text-xs text-muted-foreground/80">No modules match your search.</p>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {attachFlowIds.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {attachFlowIds.map((id) => {
                  const flow = flows.find((item) => String(item.id) === id);
                  const label = flow?.name ?? id;
                  const isRequired = attachFlowRequiredById[id] ?? true;
                  return (
                    <Badge key={id} variant="secondary" className="flex items-center gap-1 rounded-full px-2 py-1 text-xs">
                      {label}
                      <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Checkbox
                          checked={isRequired}
                          onCheckedChange={(checked) => onToggleAttachFlowRequired(id, checked)}
                          aria-label={`Mark ${label} as required`}
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        aria-label={`Remove ${label}`}
                        className="rounded-full p-0.5 hover:bg-muted/50"
                        onClick={() => onRemoveAttachFlow(id)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground/80">No modules selected yet.</p>
            )}
          </div>
        ) : (
          <p className="rounded-lg border border-border/40 bg-white/5 p-3 text-xs text-muted-foreground">
            {selected
              ? 'All available modules already include this question.'
              : 'No modules available to attach. Create a module or adjust your course filter to see options.'}
          </p>
        )}
      </div>

      {/*
        Below lg the primary navigation is fixed to the bottom of the screen. The bar is then fixed just above it,
        portalled to <body> because the frosted card's backdrop-filter would otherwise contain a fixed element, and
        shown only while the editor is on screen; the spacer keeps the end of the form scrollable above it.
        From lg the bar is sticky inside the editor.
      */}
      {bottomNavShown ? (
        <>
          <div className="h-16" aria-hidden="true" />
          {editorOnScreen
            ? createPortal(
              <div
                className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-50 flex justify-center px-4"
                data-testid="question-save-bar"
              >
                {saveBarPill}
              </div>,
              document.body,
            )
            : null}
        </>
      ) : (
        <div className="pointer-events-none sticky bottom-4 z-20 flex justify-center pt-4" data-testid="question-save-bar">
          {saveBarPill}
        </div>
      )}

      <Dialog open={reviewOpen} onOpenChange={(open) => { if (!open && !saving) closeReview(); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review changes{selected ? ` to ${selected.key}` : ''}</DialogTitle>
            <DialogDescription>
              {selected?.flows?.length && selected.flows.length > 1
                ? `This question is used in ${selected.flows.length} modules. Saving changes it in all of them.`
                : 'Nothing is saved until you confirm.'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {reviewLoading ? <p className="text-sm text-muted-foreground">Checking changes…</p> : null}
            {hasPreviewResult && answerReferenceWarnings.length ? (
              <div className="mb-3 rounded-lg border border-amber-300/60 bg-amber-500/10 p-3 text-sm" data-testid="answer-reference-review-warnings" role="status">
                <p className="mb-2 font-semibold">Earlier-answer references to check</p>
                <ul className="ml-4 list-disc space-y-1">
                  {answerReferenceWarnings.map((warning, index) => (
                    <li key={`${warning.code}-${index}`}>
                      {warning.flow_name ? <span className="font-medium">{warning.flow_name}: </span> : null}
                      {warning.message}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">You can still save. Learners see the fallback or &quot;no answer yet&quot; instead.</p>
              </div>
            ) : null}
      {hasPreviewResult ? (
              diffPreview.length > 0 ? (
                <div className="rounded-lg border border-blue-200/40 bg-blue-500/5 p-3 text-sm">
                  <p className="mb-2 font-semibold uppercase tracking-wide">Diff preview</p>
                  <ul className="ml-4 list-disc space-y-1">
                    {summaryItems.map((item) => (
                      <li key={item.key}>{item.label}</li>
                    ))}
                  </ul>
                  {diffPreviewDetails.length > 0 ? (
                    <div className="mt-3 space-y-3">
                      {diffPreviewDetails.map((detail, index) => (
                        <div key={`${detail.field}-${index}`} className="rounded border border-white/10 bg-white/5 p-3 text-xs">
                          <p className="mb-2 font-semibold uppercase tracking-wide text-muted-foreground">
                            {formatFieldLabel(detail.field)}
                          </p>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                Previous
                              </p>
                              {renderDiffValue(detail.field, detail.before, detail.after, 'before')}
                            </div>
                            <div>
                              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                New
                              </p>
                              {renderDiffValue(detail.field, detail.after, detail.before, 'after')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200/40 bg-emerald-500/5 p-3 text-sm">
                  <p className="font-semibold uppercase tracking-wide">No changes detected</p>
                  <p className="mt-1">This question matches the current saved version.</p>
                </div>
              )
            ) : null}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="auto" variant="ghost" disabled={saving} onClick={closeReview}>
              Keep editing
            </Button>
            <Button size="auto" disabled={saving || !canSave || reviewLoading || !previewReady} onClick={handleConfirmSave}>
              {saving ? 'Saving...' : 'Confirm and save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
